import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { API_BASE_URL } from '../../../core/services/api.config';
import {
  AuthUser,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  LoginRequest,
  LoginResponse,
  MeResponse,
  RefreshResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ValidateResetPasswordTokenResponse,
} from '../models/auth.models';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private static readonly SESSION_HINT_KEY = 'ovp.auth.hasSession';

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly apiBaseUrl = inject(API_BASE_URL);

  private readonly _user = signal<AuthUser | null>(null);
  private readonly _accessToken = signal<string | null>(null);
  private readonly _bootstrapped = signal(false);
  private readonly _authCheckInProgress = signal(false);
  private readonly _logoutInProgress = signal(false);

  readonly user = this._user.asReadonly();
  readonly accessToken = this._accessToken.asReadonly();
  readonly bootstrapped = this._bootstrapped.asReadonly();
  readonly authCheckInProgress = this._authCheckInProgress.asReadonly();
  readonly logoutInProgress = this._logoutInProgress.asReadonly();

  readonly isAuthenticated = computed(() => !!this._user() && !!this._accessToken());
  readonly role = computed(() => this._user()?.role ?? null);
  readonly fullName = computed(() => {
    const user = this._user();
    return user ? `${user.firstName} ${user.lastName}` : '';
  });

  hasSessionHint(): boolean {
    try {
      return localStorage.getItem(AuthService.SESSION_HINT_KEY) === '1';
    } catch {
      return false;
    }
  }

  private setSessionHint(value: boolean): void {
    try {
      if (value) {
        localStorage.setItem(AuthService.SESSION_HINT_KEY, '1');
      } else {
        localStorage.removeItem(AuthService.SESSION_HINT_KEY);
      }
    } catch {
      // Ignore storage access failures and continue with in-memory auth state.
    }
  }

  login(payload: LoginRequest): Observable<AuthUser> {
    return this.http
      .post<LoginResponse>(`${this.apiBaseUrl}/auth/login`, payload, {
        withCredentials: true,
      })
      .pipe(
        tap((response) => {
          this._accessToken.set(response.accessToken);
          this._user.set(response.user);
          this.setSessionHint(true);
          this._bootstrapped.set(true);
        }),
        map((response) => response.user),
      );
  }

  bootstrapAuth(): Observable<boolean> {
    if (this._bootstrapped()) {
      return of(this.isAuthenticated());
    }

    if (!this.hasSessionHint() && !this._accessToken()) {
      this.clearAuthState();
      this._bootstrapped.set(true);
      return of(false);
    }

    if (this._authCheckInProgress()) {
      return of(false);
    }

    this._authCheckInProgress.set(true);

    const meRequest$ = this.http.get<MeResponse>(`${this.apiBaseUrl}/auth/me`, {
      withCredentials: true,
    });

    const authRequest$ = this._accessToken()
      ? meRequest$
      : this.refreshAccessToken().pipe(switchMap(() => meRequest$));

    return authRequest$
      .pipe(
        tap((response) => {
          this._user.set(response.user);
          this._accessToken.set(response.accessToken ?? null);
          this.setSessionHint(true);
          this._bootstrapped.set(true);
        }),
        map(() => true),
        catchError(() => {
          this.clearAuthState();
          this._bootstrapped.set(true);
          return of(false);
        }),
        finalize(() => {
          this._authCheckInProgress.set(false);
        }),
      );
  }

  refreshAccessToken(): Observable<string> {
    return this.http
      .post<RefreshResponse>(
        `${this.apiBaseUrl}/auth/refresh`,
        {},
        { withCredentials: true },
      )
      .pipe(
        tap((response) => {
          this._accessToken.set(response.accessToken);
          this.setSessionHint(true);
        }),
        map((response) => response.accessToken),
      );
  }

  requestPasswordReset(
    payload: ForgotPasswordRequest,
  ): Observable<ForgotPasswordResponse> {
    return this.http.post<ForgotPasswordResponse>(
      `${this.apiBaseUrl}/auth/forgot-password`,
      payload,
      {
        withCredentials: true,
      },
    );
  }

  validateResetPasswordToken(
    token: string,
  ): Observable<ValidateResetPasswordTokenResponse> {
    return this.http.post<ValidateResetPasswordTokenResponse>(
      `${this.apiBaseUrl}/auth/reset-password/validate`,
      { token },
      {
        withCredentials: true,
      },
    );
  }

  resetPassword(
    payload: ResetPasswordRequest,
  ): Observable<ResetPasswordResponse> {
    return this.http.post<ResetPasswordResponse>(
      `${this.apiBaseUrl}/auth/reset-password`,
      payload,
      {
        withCredentials: true,
      },
    );
  }

  logout(): Observable<void> {
    if (this._logoutInProgress()) {
      return of(void 0);
    }

    this._logoutInProgress.set(true);

    return this.http
      .post<void>(
        `${this.apiBaseUrl}/auth/logout`,
        {},
        { withCredentials: true },
      )
      .pipe(
        catchError(() => of(void 0)),
        tap(() => {
          this.clearAuthState();
          this._bootstrapped.set(true);
        }),
        tap(() => {
          void this.router.navigate(['/login']);
        }),
        finalize(() => {
          this._logoutInProgress.set(false);
        }),
      );
  }

  setAccessToken(token: string | null): void {
    this._accessToken.set(token);
  }

  clearAuthState(): void {
    this._user.set(null);
    this._accessToken.set(null);
    this.setSessionHint(false);
  }
}